import React, { useState, useEffect } from 'react';
import { formatDate, getGameStatus } from '../../utils/helpers';
import { Plus } from 'lucide-react';

const EditGame = ({
  game,
  teams,
  tournaments,
  onBack,
  onSave,
  isAdmin = false
}) => {
  // Determine initial modes based on whether team IDs exist in the teams list
  const getInitialMode = (teamId, teamName) => {
    if (!teamId) return 'custom'; // No ID means it was a custom team
    const teamExists = teams.find(t => t.id === teamId);
    return teamExists ? 'dropdown' : 'custom';
  };

  const [homeTeamMode, setHomeTeamMode] = useState(getInitialMode(game.homeTeamId, game.homeTeam));
  const [awayTeamMode, setAwayTeamMode] = useState(getInitialMode(game.awayTeamId, game.awayTeam));

  const initialTournament = game.tournamentId ? tournaments.find(t => t.id === game.tournamentId) : null;

  const [formData, setFormData] = useState({
    homeTeamId: game.homeTeamId || '',
    homeTeamCustom: !game.homeTeamId || !teams.find(t => t.id === game.homeTeamId) ? (game.homeTeam || '') : '',
    awayTeamId: game.awayTeamId || '',
    awayTeamCustom: !game.awayTeamId || !teams.find(t => t.id === game.awayTeamId) ? (game.awayTeam || '') : '',
    date: game.date || '',
    time: game.time || '',
    arrivalTime: game.arrivalTime || '',
    lateFeeCutoffTime: game.lateFeeCutoffTime || '',
    lateFeeAmount: game.lateFeeAmount || 5,
    venue: game.venue || '',
    tournamentId: game.tournamentId || '',
    askMealPreference: game.askMealPreference || false,
    
    // Squad settings: ALWAYS inherit from tournament if available, otherwise fallback to game settings
    maxSquadSize: initialTournament?.maxSquadSize !== undefined 
      ? initialTournament.maxSquadSize 
      : (game.maxSquadSize !== undefined ? game.maxSquadSize : 0),
    allowSuperSubs: initialTournament?.allowSuperSubs !== undefined
      ? initialTournament.allowSuperSubs
      : (game.allowSuperSubs || false),

    // Game Status
    status: game.status || '' // Allow empty to default to Auto / Scheduled
  });

  const [selectedTournament, setSelectedTournament] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-fill arrival time (30 mins prior) and late fee cutoff (15 mins prior) IF user changes game time
  const [initialTimeSet, setInitialTimeSet] = useState(false);
  useEffect(() => {
    if (formData.time) {
      if (!initialTimeSet) {
        setInitialTimeSet(true);
        // If it's the first load, don't override existing times if both exist AND are valid
        if (
          formData.arrivalTime && !String(formData.arrivalTime).includes('NaN') &&
          formData.lateFeeCutoffTime && !String(formData.lateFeeCutoffTime).includes('NaN')
        ) return;
      }

      try {
        const timeMatch = formData.time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);
        if (!timeMatch) return;

        let hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        const ampm = timeMatch[3];

        if (ampm) {
          if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
          if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
        }

        if (isNaN(hours) || isNaN(minutes)) return;

        // Calculate arrival time (30 mins before)
        const arrivalDate = new Date();
        arrivalDate.setHours(hours, minutes, 0, 0);
        arrivalDate.setMinutes(arrivalDate.getMinutes() - 30);
        const arrivalHours = String(arrivalDate.getHours()).padStart(2, '0');
        const arrivalMinutes = String(arrivalDate.getMinutes()).padStart(2, '0');
        const calculatedArrival = `${arrivalHours}:${arrivalMinutes}`;

        // Calculate late fee cutoff time (30 mins before, matching arrival)
        const cutoffDate = new Date();
        cutoffDate.setHours(hours, minutes, 0, 0);
        cutoffDate.setMinutes(cutoffDate.getMinutes() - 30);
        const cutoffHours = String(cutoffDate.getHours()).padStart(2, '0');
        const cutoffMinutes = String(cutoffDate.getMinutes()).padStart(2, '0');
        const calculatedCutoff = `${cutoffHours}:${cutoffMinutes}`;

        setFormData(prev => ({
          ...prev,
          arrivalTime: (!initialTimeSet && prev.arrivalTime && !String(prev.arrivalTime).includes('NaN')) ? prev.arrivalTime : calculatedArrival,
          lateFeeCutoffTime: (!initialTimeSet && prev.lateFeeCutoffTime && !String(prev.lateFeeCutoffTime).includes('NaN')) ? prev.lateFeeCutoffTime : calculatedCutoff
        }));
      } catch (e) {
        console.error('Error calculating times', e);
      }
    }
  }, [formData.time]);

  useEffect(() => {
    if (formData.tournamentId) {
      const tournament = tournaments.find(t => t.id === formData.tournamentId);
      setSelectedTournament(tournament);
    } else {
      setSelectedTournament(null);
    }
  }, [formData.tournamentId, tournaments]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Switch between dropdown and custom input modes
  const toggleHomeTeamMode = () => {
    setHomeTeamMode(prev => prev === 'dropdown' ? 'custom' : 'dropdown');
    setFormData(prev => ({ ...prev, homeTeamId: '', homeTeamCustom: '' }));
  };

  const toggleAwayTeamMode = () => {
    setAwayTeamMode(prev => prev === 'dropdown' ? 'custom' : 'dropdown');
    setFormData(prev => ({ ...prev, awayTeamId: '', awayTeamCustom: '' }));
  };

  // Get the effective team name and ID based on mode
  const getHomeTeam = () => {
    if (homeTeamMode === 'dropdown' && formData.homeTeamId) {
      const team = teams.find(t => t.id === formData.homeTeamId);
      return { id: formData.homeTeamId, name: team?.name || '' };
    } else if (homeTeamMode === 'custom' && formData.homeTeamCustom.trim()) {
      const customName = formData.homeTeamCustom.trim();
      const isOriginalName = customName === game.homeTeam;
      return { id: isOriginalName ? game.homeTeamId : null, name: customName };
    }
    return { id: null, name: '' };
  };

  const getAwayTeam = () => {
    if (awayTeamMode === 'dropdown' && formData.awayTeamId) {
      const team = teams.find(t => t.id === formData.awayTeamId);
      return { id: formData.awayTeamId, name: team?.name || '' };
    } else if (awayTeamMode === 'custom' && formData.awayTeamCustom.trim()) {
      const customName = formData.awayTeamCustom.trim();
      const isOriginalName = customName === game.awayTeam;
      return { id: isOriginalName ? game.awayTeamId : null, name: customName };
    }
    return { id: null, name: '' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const homeTeam = getHomeTeam();
    const awayTeam = getAwayTeam();

    if (!homeTeam.name || !awayTeam.name) {
      alert('Please provide both home and away teams');
      return;
    }

    if (homeTeam.name === awayTeam.name) {
      alert('Home team and away team cannot be the same');
      return;
    }

    if (!formData.date) {
      alert('Please select a date');
      return;
    }

    // Validate date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(formData.date);
    selectedDate.setHours(0, 0, 0, 0);

    if (!isAdmin && selectedDate < today) {
      alert('Game date cannot be in the past. Please select today or a future date.');
      return;
    }

    setIsLoading(true);

    try {
      const tournamentName = selectedTournament ? selectedTournament.name : null;

      // Generate game name from teams
      const gameName = `${homeTeam.name} vs ${awayTeam.name}`;

      const gameData = {
        name: gameName || 'Unknown Game',
        date: formData.date || null,
        time: formData.time || null,
        arrivalTime: formData.arrivalTime || null,
        lateFeeCutoffTime: formData.lateFeeCutoffTime || null,
        lateFeeAmount: Number(formData.lateFeeAmount) || 5,
        venue: formData.venue || null,
        tournamentId: formData.tournamentId || null,
        tournamentName: tournamentName || null,
        homeTeam: homeTeam.name || '',
        awayTeam: awayTeam.name || '',
        homeTeamId: homeTeam.id || null, // Will be null for custom teams
        awayTeamId: awayTeam.id || null, // Will be null for custom teams
        opponent: awayTeam.name || '', // For compatibility
        askMealPreference: formData.askMealPreference ?? false,
        // Squad settings at game level
        maxSquadSize: formData.maxSquadSize ?? 0,
        allowSuperSubs: formData.allowSuperSubs ?? false,
        status: formData.status || null,
        updatedAt: new Date().toISOString()
      };

      await onSave(game.id, gameData);
    } catch (error) {
      console.error('Error updating game:', error);
      alert('Error updating game');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine available teams based on selected tournament
  const availableTeams = selectedTournament?.teams?.length > 0
    ? teams.filter(t => selectedTournament.teams.includes(t.id))
    : teams;

  // Helper to render team input (dropdown or custom)
  const renderTeamInput = (type) => {
    const isHome = type === 'home';
    const mode = isHome ? homeTeamMode : awayTeamMode;
    const toggleMode = isHome ? toggleHomeTeamMode : toggleAwayTeamMode;
    const teamId = isHome ? formData.homeTeamId : formData.awayTeamId;
    const customName = isHome ? formData.homeTeamCustom : formData.awayTeamCustom;
    const otherTeamId = isHome ? formData.awayTeamId : formData.homeTeamId;
    const fieldId = isHome ? 'homeTeamId' : 'awayTeamId';
    const fieldCustom = isHome ? 'homeTeamCustom' : 'awayTeamCustom';

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-700">
            {isHome ? 'Home Team' : 'Away Team'} <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={toggleMode}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            {mode === 'dropdown' ? (
              <>
                <Plus size={12} />
                Add custom team
              </>
            ) : (
              <>
                ← Select from list
              </>
            )}
          </button>
        </div>

        {mode === 'dropdown' ? (
          <select
            value={teamId}
            onChange={(e) => handleChange(fieldId, e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Select {isHome ? 'Home' : 'Away'} Team</option>
            {availableTeams.map(team => (
              <option
                key={team.id}
                value={team.id}
                disabled={team.id === otherTeamId}
              >
                {team.name}
              </option>
            ))}
          </select>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={customName}
              onChange={(e) => handleChange(fieldCustom, e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 border-blue-300 bg-blue-50"
              placeholder={`Enter ${isHome ? 'home' : 'away'} team name (e.g., Seattle Cricket Club)`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium">
              Custom
            </span>
          </div>
        )}

        {mode === 'custom' && (
          <p className="text-xs text-blue-600 mt-1">
            💡 Type any team name - useful for opponent teams from other clubs
          </p>
        )}
      </div>
    );
  };

  // Get current team names for summary display
  const homeTeam = getHomeTeam();
  const awayTeam = getAwayTeam();

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={onBack}
        className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
      >
        ← Back to Games
      </button>

      <div className="bg-white rounded-xl shadow-sm p-8">
        <h2 className="text-2xl font-bold mb-6">Edit Game</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Home Team vs Away Team */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderTeamInput('home')}
            {renderTeamInput('away')}
          </div>

          {/* VS indicator when both teams are selected */}
          {homeTeam.name && awayTeam.name && (
            <div className="text-center py-2">
              <span className="inline-flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-full text-sm">
                <span className="font-semibold text-gray-800">{homeTeam.name}</span>
                <span className="text-gray-400 font-bold">VS</span>
                <span className="font-semibold text-gray-800">{awayTeam.name}</span>
              </span>
            </div>
          )}

          {/* Tournament Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tournament (Optional)
            </label>
            <select
              value={formData.tournamentId}
              onChange={(e) => {
                const newTournamentId = e.target.value;
                handleChange('tournamentId', newTournamentId);
                // Auto-inherit squad settings when a tournament is selected
                if (newTournamentId) {
                  const tournament = tournaments.find(t => t.id === newTournamentId);
                  if (tournament) {
                    if (tournament.maxSquadSize !== undefined) {
                      handleChange('maxSquadSize', tournament.maxSquadSize);
                    }
                    if (tournament.allowSuperSubs !== undefined) {
                      handleChange('allowSuperSubs', tournament.allowSuperSubs);
                    }
                  }
                }
              }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">No Tournament (Friendly/Practice)</option>
              {tournaments.map(tournament => (
                <option key={tournament.id} value={tournament.id}>
                  {tournament.name}
                </option>
              ))}
            </select>
          </div>

          {/* Squad Size Settings */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Squad Size
            </label>
            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => {
                  handleChange('maxSquadSize', 8);
                  const tournament = tournaments?.find(t => t.id === formData.tournamentId);
                  handleChange('allowSuperSubs', tournament ? !!tournament.allowSuperSubs : false);
                }}
                className={`p-3 rounded-lg border-2 text-center transition-all ${formData.maxSquadSize === 8
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <span className="text-lg font-bold">8</span>
                <p className="text-xs text-gray-500">Tennis</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleChange('maxSquadSize', 11);
                  const tournament = tournaments?.find(t => t.id === formData.tournamentId);
                  handleChange('allowSuperSubs', tournament ? !!tournament.allowSuperSubs : false);
                }}
                className={`p-3 rounded-lg border-2 text-center transition-all ${formData.maxSquadSize === 11
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <span className="text-lg font-bold">XI</span>
                <p className="text-xs text-gray-500">Playing 11</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleChange('maxSquadSize', 12);
                  handleChange('allowSuperSubs', true);
                }}
                className={`p-3 rounded-lg border-2 text-center transition-all ${formData.maxSquadSize === 12
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <span className="text-lg font-bold">XII</span>
                <p className="text-xs text-gray-500">+ Super Subs</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleChange('maxSquadSize', 0);
                  handleChange('allowSuperSubs', false);
                }}
                className={`p-3 rounded-lg border-2 text-center transition-all ${formData.maxSquadSize === 0
                  ? 'border-orange-500 bg-orange-50 text-orange-700'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <span className="text-lg font-bold">∞</span>
                <p className="text-xs text-gray-500">Custom</p>
              </button>
            </div>
            {formData.allowSuperSubs && (
              <p className="text-xs text-purple-600 mt-2">
                ⭐ Up to 2 super subs allowed (pay 50% of expenses)
              </p>
            )}
            {formData.maxSquadSize === 0 && (
              <p className="text-xs text-orange-600 mt-2">
                ∞ Any number of players allowed. Expenses divided equally among all.
              </p>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Games can only be scheduled for today or future dates</p>
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Time
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Arrival Time
              </label>
              <input
                type="time"
                value={formData.arrivalTime}
                onChange={(e) => handleChange('arrivalTime', e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Late Fee Cutoff Time
              </label>
              <input
                type="time"
                value={formData.lateFeeCutoffTime}
                onChange={(e) => handleChange('lateFeeCutoffTime', e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Late Fee Amount ($)
              </label>
              <input
                type="number"
                min="0"
                value={formData.lateFeeAmount}
                onChange={(e) => handleChange('lateFeeAmount', e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Game Status Override */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Game Status
            </label>
            <div className="flex gap-4 items-center">
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className={`w-full md:w-1/2 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.status ? 'bg-white' :
                  formData.status === 'Cancelled' ? 'bg-red-50 border-red-200 text-red-900' :
                    formData.status === 'Abandoned' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                      'bg-green-50 border-green-200 text-green-900'
                  }`}
              >
                <option value="">Auto (Scheduled / Completed)</option>
                <option value="Completed">Completed (Force early)</option>
                <option value="Abandoned">Abandoned / Rained Out</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 Games automatically switch to "Completed" at the end of their scheduled day. Use this dropdown to manually override if a game was abandoned or cancelled.
            </p>
          </div>

          {/* Venue/Ground Location */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Ground Location
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.venue}
                onChange={(e) => handleChange('venue', e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pr-24"
                placeholder="e.g., Discovery Park, Seattle or full address"
              />
              {formData.venue && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.venue)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-xs font-semibold"
                  title="Preview location in Google Maps"
                >
                  📍 Preview
                </a>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              💡 Tip: Enter full address or landmark name. Players can click to view on maps.
            </p>
          </div>

          {/* Meal Preference Option */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.askMealPreference}
                onChange={(e) => handleChange('askMealPreference', e.target.checked)}
                className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <div>
                <span className="font-semibold text-gray-900">🍽️ Ask for meal preferences</span>
                <p className="text-sm text-gray-600 mt-0.5">
                  Players will be asked to select their meal preference (Veg/Non-Veg/No Meal) along with availability
                </p>
              </div>
            </label>
          </div>

          {/* Summary */}
          {(homeTeam.name || awayTeam.name || formData.date) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 text-sm mb-2">Game Summary:</h4>
              <div className="text-sm text-blue-800 space-y-1">
                {homeTeam.name && awayTeam.name && (
                  <p className="text-lg font-bold">
                    🏏 {homeTeam.name} vs {awayTeam.name}
                  </p>
                )}
                {formData.date && (
                  <p>📅 {formatDate(formData.date, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</p>
                )}
                {formData.time && <p>🕐 Start: {formData.time}</p>}
                {formData.arrivalTime && <p>🕐 Arrival: {formData.arrivalTime}</p>}
                {formData.lateFeeCutoffTime && <p>⏰ Late Cutoff: {formData.lateFeeCutoffTime}</p>}
                {formData.lateFeeAmount && <p>💲 Late Fee: ${formData.lateFeeAmount}</p>}
                {formData.tournamentId && selectedTournament && (
                  <p>🏆 Tournament: {selectedTournament.name}</p>
                )}
                {formData.venue && <p>📍 {formData.venue}</p>}
                {formData.askMealPreference && <p>🍽️ Meal preferences will be collected</p>}

                {/* Show which teams are custom */}
                {(homeTeamMode === 'custom' || awayTeamMode === 'custom') && (
                  <p className="text-xs text-blue-600 mt-2 pt-2 border-t border-blue-200">
                    ℹ️ {homeTeamMode === 'custom' && awayTeamMode === 'custom'
                      ? 'Both teams are external (custom names)'
                      : homeTeamMode === 'custom'
                        ? 'Home team is external (custom name)'
                        : 'Away team is external (custom name)'}
                  </p>
                )}

                {/* Visual Status Indicator in Summary */}
                <div className="mt-2 pt-2 border-t border-blue-200 flex items-center gap-2">
                  <span className="font-semibold text-blue-900">Current Status:</span>
                  {getGameStatus({ ...game, ...formData, status: formData.status }) === 'Scheduled' && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold">Scheduled</span>
                  )}
                  {getGameStatus({ ...game, ...formData, status: formData.status }) === 'Completed' && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold">Completed</span>
                  )}
                  {getGameStatus({ ...game, ...formData, status: formData.status }) === 'Abandoned' && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded font-semibold">Abandoned</span>
                  )}
                  {getGameStatus({ ...game, ...formData, status: formData.status }) === 'Cancelled' && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-semibold">Cancelled</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {isLoading ? 'Updating...' : 'Update Game'}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGame;